"use client"

/**
 * Auction-winner payment surface. Thin wrapper around the shared
 * <PaymentSheet> shell — owns the auction FSM (useViewerAuctionState) and the
 * /current-bid pre-fetch (refreshes winningAmount / winnerId / auctionStatus /
 * paymentDeadline / existing-order pre-check on open). The shell handles
 * listing-meta + checkout form + retry-settle UI.
 *
 * @see components/checkout/payment-sheet.tsx — shared shell
 * @see hooks/use-viewer-auction-state.ts — FSM (issue #156)
 * @see lib/auctions/auction-builders.ts — settle builder (canonical, G19)
 */

import { useCallback, useEffect, useMemo, useState } from "react"
import type { RawImageRef } from "@kraahl/contract"
import { AlertTriangle } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  AuctionCurrentBidResponseSchema,
  parseApiResponse,
  type AuctionCurrentBidResponse,
} from "@kraahl/contract"
import { useViewerAuctionState } from "@/hooks/use-viewer-auction-state"
import type { ViewerAuctionBaseline } from "@/lib/auction-viewer-state"
import { settleAuction as settleAuctionBuilder } from "@/lib/auctions/auction-builders"
import {
  PaymentSheet,
  type PaymentSheetGate,
  type PaymentSheetVariant,
} from "@/components/checkout/payment-sheet"

interface AuctionWinnerCheckoutSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  listingId: string
  userId: string
  // FIX-5: winnerId for winner guard
  winnerId?: string
  winningAmount: number
  listingTitle: string
  /** STORED refs — snapshotted into `order_items.image_url` (handoff §9). */
  listingImages: RawImageRef[]
  sellerId: string
  sellerName: string
  /** `profiles.avatar_url` as stored. */
  sellerAvatarUrl?: RawImageRef
  condition: string
  description: string
  grade: string
  currency: string
  auctionStatus?: string
}

async function fetchCurrentBid(listingId: string): Promise<AuctionCurrentBidResponse | null> {
  try {
    const r = await fetch(`/api/auctions/${encodeURIComponent(listingId)}/current-bid`)
    if (!r.ok) return null
    return await parseApiResponse(r, AuctionCurrentBidResponseSchema)
  } catch {
    return null
  }
}

export function AuctionWinnerCheckoutSheet({
  open,
  onOpenChange,
  listingId,
  userId,
  winnerId,
  winningAmount,
  listingTitle,
  listingImages,
  sellerId,
  sellerName,
  sellerAvatarUrl,
  condition,
  description,
  grade,
  currency,
  auctionStatus,
}: AuctionWinnerCheckoutSheetProps) {
  // FIX-9 / M-4: refresh winning amount + winner id from DB on open. SSR
  // props can be stale after a soft-close bid arrived between SSR and the
  // user opening the sheet.
  const [currentWinningAmount, setCurrentWinningAmount] = useState(winningAmount)
  const [currentWinnerId, setCurrentWinnerId] = useState<string | undefined>(winnerId)
  // Server-truth flags from /current-bid — fed into the FSM as overrides.
  const [serverReserveNotMet, setServerReserveNotMet] = useState<boolean | undefined>(undefined)
  const [serverDeadlineExpired, setServerDeadlineExpired] = useState<boolean | undefined>(undefined)
  // Fresh auction status + payment deadline (post-fetch).
  const [freshAuctionStatus, setFreshAuctionStatus] = useState<string | null>(null)
  const [freshPaymentDeadline, setFreshPaymentDeadline] = useState<string | null>(null)
  // Open-time existing-order pre-check (double-charge guard). Passes through
  // to the shell's `initialPendingOrderId`.
  const [initialPendingOrderId, setInitialPendingOrderId] = useState<string | null>(null)
  const [bidFetched, setBidFetched] = useState(false)

  // FSM baseline. The FSM is the single source of truth for "can this user
  // pay?" — see issue #156. All branch decisions below switch on
  // `viewerState.kind` rather than re-derive booleans.
  const auctionBaseline: ViewerAuctionBaseline = useMemo(
    () => ({
      startPrice: 0,
      reservePrice: null, // injected via overrides.reserveNotMet
      endAt: null, // sheet only opens for ended auctions
      status: freshAuctionStatus ?? auctionStatus ?? "ended",
      highestBid: currentWinningAmount,
      highestBidderId: currentWinnerId ?? null,
      bidCount: 1, // ensures FSM treats as "has bids"
      paymentDeadline: freshPaymentDeadline,
    }),
    [freshAuctionStatus, auctionStatus, currentWinningAmount, currentWinnerId, freshPaymentDeadline],
  )

  const viewerState = useViewerAuctionState({
    listingId,
    currentUserId: userId,
    auction: auctionBaseline,
    sellerId,
    existingOrderId: initialPendingOrderId,
    overrides: {
      reserveNotMet: serverReserveNotMet,
      deadlineExpired: serverDeadlineExpired,
    },
  })

  useEffect(() => {
    if (!open || !listingId) return
    let cancelled = false
    setBidFetched(false)
    setInitialPendingOrderId(null)
    setServerDeadlineExpired(undefined)
    setServerReserveNotMet(undefined)
    setFreshPaymentDeadline(null)

    fetchCurrentBid(listingId).then((bidData) => {
      if (cancelled) return
      if (bidData && bidData.highestBid !== null) setCurrentWinningAmount(bidData.highestBid)
      if (bidData && bidData.highestBidderId !== null) setCurrentWinnerId(bidData.highestBidderId)
      if (bidData && bidData.auctionStatus !== null) setFreshAuctionStatus(bidData.auctionStatus)
      if (bidData && bidData.paymentDeadline !== null) setFreshPaymentDeadline(bidData.paymentDeadline)
      // Server-truth booleans → FSM overrides.
      if (bidData?.deadlineExpired) {
        setServerDeadlineExpired(true)
      } else if (bidData?.reserveNotMet) {
        setServerReserveNotMet(true)
      } else if (bidData && bidData.existingOrderId !== null) {
        // Double-charge guard.
        setInitialPendingOrderId(bidData.existingOrderId)
      }
      setBidFetched(true)
    })

    return () => {
      cancelled = true
    }
  }, [open, listingId])

  const close = useCallback(() => onOpenChange(false), [onOpenChange])

  const settleAuction = useCallback(
    async (orderId: string): Promise<boolean> => {
      try {
        // G19 producer: builder lives in `lib/auctions/auction-builders.ts`.
        const result = await settleAuctionBuilder({ listingId, orderId })
        if (!result.ok) {
          const err = (result.data ?? {}) as { error?: string }
          if (
            result.status === 409 &&
            (err.error === "Auction already settled" ||
              err.error === "Auction already settled or status changed")
          ) {
            return true
          }
          if (result.status === 409 && err.error === "Payment deadline expired") {
            setServerDeadlineExpired(true)
          }
          console.error("[auction-checkout] settlement failed", result.status, err)
          return false
        }
        return true
      } catch (err) {
        console.error("[auction-checkout] settlement failed", err)
        return false
      }
    },
    [listingId],
  )

  // Gate evaluation. FSM-driven for the auction kinds; "loading" until the
  // /current-bid call lands (we need fresh data before deciding). The
  // catch-all "Not available" matches the previous FIX-5 defense-in-depth.
  const gate: PaymentSheetGate = useMemo(() => {
    if (!bidFetched) return { kind: "loading" }

    if (viewerState.kind === "ended_payment_expired") {
      return {
        kind: "blocked",
        body: (
          <div className="flex flex-col items-center justify-center py-16 text-center px-4">
            <AlertTriangle className="h-10 w-10 text-red-500 mb-4" />
            <p className="text-lg font-semibold">Payment deadline expired</p>
            <p className="text-sm text-neutral-400 mt-2">
              The payment window for this auction has closed. Please contact the seller if you believe
              this is an error.
            </p>
            {initialPendingOrderId && (
              <p className="text-xs text-neutral-500 mt-3 font-mono select-all">
                Order ID: {initialPendingOrderId}
              </p>
            )}
            <Button variant="ghost" className="mt-4" onClick={close}>
              Close
            </Button>
          </div>
        ),
      }
    }

    if (viewerState.kind === "ended_reserve_not_met") {
      return {
        kind: "blocked",
        body: (
          <div className="flex flex-col items-center justify-center py-16 text-center px-4">
            <AlertTriangle className="h-10 w-10 text-amber-500 mb-4" />
            <p className="text-lg font-semibold">Reserve price not met</p>
            <p className="text-sm text-neutral-400 mt-2">
              Your bid was the highest, but it did not meet the seller&apos;s reserve price. No payment
              is owed.
            </p>
            <Button variant="ghost" className="mt-4" onClick={close}>
              Close
            </Button>
          </div>
        ),
      }
    }

    // "Already paid" before retry UI: in the race window where /current-bid
    // returned an existingOrderId (auction was not yet settled) but realtime
    // has since flipped to "settled", we want the clean terminal state.
    if (viewerState.kind === "ended_won_settled") {
      return {
        kind: "blocked",
        body: (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-lg font-semibold">Already paid</p>
            <p className="text-sm text-neutral-400 mt-2">This auction has been settled.</p>
            <Button variant="ghost" className="mt-4" onClick={close}>
              Close
            </Button>
          </div>
        ),
      }
    }

    // If a pending order id is set, the shell handles the retry UI. Stay
    // "ready" so the shell can take over.
    if (initialPendingOrderId) return { kind: "ready" }

    // Catch-all for non-winner states (ended_lost, ended_no_bids,
    // ended_seller, live, scheduled) — defense-in-depth.
    if (viewerState.kind !== "ended_won_pending_payment") {
      return {
        kind: "blocked",
        body: (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-lg font-semibold">Not available</p>
            <p className="text-sm text-neutral-400 mt-2">
              Only the auction winner can complete payment.
            </p>
            <Button variant="ghost" className="mt-4" onClick={close}>
              Close
            </Button>
          </div>
        ),
      }
    }

    return { kind: "ready" }
  }, [bidFetched, viewerState.kind, initialPendingOrderId, close])

  const variant: PaymentSheetVariant = useMemo(
    () => ({
      type: "auction",
      listingId,
      userId,
      sellerId,
      currency,
      item: {
        id: `auction-win-${listingId}`,
        title: listingTitle,
        description,
        condition,
        grade,
        images: listingImages,
        sellerName,
        sellerAvatarUrl,
        // FIX-9: use refreshed amount
        winningAmount: currentWinningAmount,
      },
    }),
    [
      listingId,
      userId,
      sellerId,
      currency,
      listingTitle,
      description,
      condition,
      grade,
      listingImages,
      sellerName,
      sellerAvatarUrl,
      currentWinningAmount,
    ],
  )

  return (
    <PaymentSheet
      open={open}
      onOpenChange={onOpenChange}
      variant={variant}
      gate={gate}
      title="Pay — Auction won"
      onSettle={settleAuction}
      initialPendingOrderId={initialPendingOrderId}
      merchantCopy={{
        title: "Payment not available",
        description:
          "The seller's payment account is not ready to receive payments. Please contact the seller or try again later.",
        closeLabel: "Close",
        detail: "Seller cannot receive payments",
      }}
    />
  )
}
